/**
 * React Query shared utilities
 * ============================
 * Reusable error handling and mutation helpers for the Redux → React Query
 * migration. These replicate and extend the behaviour of the existing
 * `rtkQueryErrorLogger` middleware so every React Query consumer gets the
 * same user-facing toast treatment.
 *
 * Convention for future migration slices:
 *   - Import `handleQueryError` and pass it as `onError` (or use the global
 *     default on the QueryClient — see queryClient.ts).
 *   - Use `createMutationOptions` when you need optimistic updates + cache
 *     invalidation in a single, consistent shape.
 */

import { t } from '@lingui/core/macro';
import toast from 'react-hot-toast';
import type { QueryClient } from '@tanstack/react-query';
import { OneServiceError } from 'src/cqrs/client/transport/errors';

// ---------------------------------------------------------------------------
// Error classification
// ---------------------------------------------------------------------------

/**
 * Returns true when the error represents a server-side failure (status >= 500
 * or an equivalent OneService graph code). This mirrors the condition in
 * `rtkQueryErrorLogger`: toast only on server errors, not on 4xx / validation.
 */
export function isServerError(error: unknown): boolean {
  if (
    error &&
    typeof error === 'object' &&
    (('code' in error &&
      (error as { code?: string }).code === 'ERR_CANCELED') ||
      ('message' in error &&
        (error as { message?: string }).message === 'Not authenticated'))
  ) {
    return false;
  }

  // OneServiceError from the CQRS layer (most common path)
  if (error instanceof OneServiceError) {
    const code = error.code;
    return code === 'E_SERVICE_ERROR' || code === 'E_UNKNOWN';
  }

  // Axios-style errors (used by the legacy `api.http` layer)
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    typeof (error as any).response?.status === 'number'
  ) {
    return (error as any).response.status >= 500;
  }

  // Generic errors with a numeric `status` property
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as any).status === 'number'
  ) {
    return (error as any).status >= 500;
  }

  // Unknown / network errors — treat as server-side (matches rtkQueryErrorLogger
  // which toasts when `status` is missing or >= 500).
  return true;
}

// ---------------------------------------------------------------------------
// Shared error handler
// ---------------------------------------------------------------------------

/**
 * Global `onError` handler for React Query queries & mutations.
 * Shows a toast for server errors (>= 500), replicating `rtkQueryErrorLogger`.
 *
 * Usage:
 *   - Set as the global default in `queryClient.ts` (already done).
 *   - Or pass explicitly: `useQuery({ ..., meta: { onError: handleQueryError } })`
 *   - Or call directly in a per-hook `onError` callback when you need custom
 *     logic on top: `onError(err) { handleQueryError(err); doExtra(); }`
 */
export function handleQueryError(error: unknown): void {
  if (isServerError(error)) {
    toast.error(t`Network error`);
  }
  // Non-server errors (4xx, validation) are intentionally silent at the global
  // level. Individual hooks can add their own toast in a local `onError`.
}

// ---------------------------------------------------------------------------
// Mutation helper
// ---------------------------------------------------------------------------

/**
 * Builds standardised mutation options for the most common pattern:
 *   1. Optimistic update (optional) — update the cache before the server
 *      responds and roll back on error.
 *   2. Cache invalidation on success — broad or scoped.
 *   3. Error toast via `handleQueryError`.
 *
 * Example:
 * ```ts
 * const mutation = useMutation({
 *   mutationFn: (payload) => api.updateSite(payload),
 *   ...createMutationOptions(queryClient, {
 *     invalidateKeys: [queryKeys.sites.all],
 *     onSuccess: () => toast.success('Site updated'),
 *   }),
 * });
 * ```
 */
export function createMutationOptions<TData = unknown, TVariables = unknown>(
  queryClient: QueryClient,
  opts: {
    /** Query keys to invalidate after a successful mutation. */
    invalidateKeys?: readonly (readonly unknown[])[];
    /** Additional success callback (runs after cache invalidation). */
    onSuccess?: (data: TData, variables: TVariables) => void;
    /** Error callback that replaces the global error toast when provided. */
    onError?: (error: unknown, variables: TVariables) => void;
    /**
     * Optimistic update: called before the mutation fires. Should return a
     * rollback function that restores the previous cache state on error.
     */
    optimisticUpdate?: (variables: TVariables) => (() => void) | void;
  } = {}
) {
  type Context = { rollback?: () => void };

  return {
    onMutate: opts.optimisticUpdate
      ? async (variables: TVariables): Promise<Context> => {
          // Cancel outgoing refetches so they don't overwrite our optimistic data.
          if (opts.invalidateKeys) {
            await Promise.all(
              opts.invalidateKeys.map(key =>
                queryClient.cancelQueries({ queryKey: key as unknown[] })
              )
            );
          }
          const rollback = opts.optimisticUpdate!(variables) ?? undefined;
          return { rollback };
        }
      : undefined,

    onSuccess: async (data: TData, variables: TVariables) => {
      // Invalidate related caches so lists/details refetch.
      if (opts.invalidateKeys) {
        await Promise.all(
          opts.invalidateKeys.map(key =>
            queryClient.invalidateQueries({ queryKey: key as unknown[] })
          )
        );
      }
      opts.onSuccess?.(data, variables);
    },

    onError: (error: unknown, variables: TVariables, context?: Context) => {
      // Roll back optimistic update if one was applied.
      context?.rollback?.();
      if (opts.onError) {
        opts.onError(error, variables);
      } else {
        handleQueryError(error);
      }
    },
  };
}
